import React, { useState } from "react";
import DraggablePanel from "./DraggablePanel";
import { Clipboard } from "lucide-react";
import { getMujocoWasmRuntimeInfo, usePhysicsStore } from "../../stores/physicsStore";
import { useSceneStore } from "../../stores/sceneStore";
import { getPhysicsWorker } from "../../physics/workerHandle";
import {
  buildPhysicsFailureReportText,
  copyTextToClipboard,
} from "../../physics/physicsFailureReport";
import {
  isPhysicsCapabilityUnavailableError,
  physicsErrorTitle,
  physicsUserActionMessage,
} from "../../stores/physics/physicsUserErrors";
import PhysicsDevToolsSection from "./physics-panel/PhysicsDevToolsSection";
import PhysicsPanelPerformanceSection from "./physics-panel/PhysicsPanelPerformanceSection";
import PhysicsRuntimeControlsSection from "./physics-panel/PhysicsRuntimeControlsSection";
import { logger } from "../../utils/logger";

const PhysicsRuntimePanel = ({
  open,
  onClose,
  motorGain,
  setMotorGain,
  titleIcon,
  mobilePinPanelId,
}) => {
  const showDevTools = import.meta.env.VITE_SHOW_DEV_TOOLS === "true";
  const engine = usePhysicsStore((s) => s.engine);
  const mapped = usePhysicsStore((s) => s.mappedBodies);
  const total = usePhysicsStore((s) => s.totalBodies);
  const status = usePhysicsStore((s) => s.status);
  const startupStartedAt = usePhysicsStore((s) => s.startupStartedAt);
  const realtimeScale = usePhysicsStore((s) => s.realtimeScale);
  const setRealtimeScale = usePhysicsStore((s) => s.actions.setRealtimeScale);
  const isRunning = usePhysicsStore((s) => s.isRunning);
  const isPaused = usePhysicsStore((s) => s.isPaused);
  const lastError = usePhysicsStore((s) => s.lastError);
  const diagnostics = usePhysicsStore((s) => s.diagnostics);
  const sceneBodyCount = useSceneStore((s) => Object.keys(s.bodies || {}).length);
  const worker = getPhysicsWorker();
  const exportWorkerXml = usePhysicsStore((s) => s.actions.exportWorkerXml);
  const poseNoGroupConversion = usePhysicsStore((s) => s.poseNoGroupConversion);
  const setPoseNoGroupConversion = usePhysicsStore((s) => s.actions.setPoseNoGroupConversion);
  const loadMode = usePhysicsStore((s) => s.loadMode);
  const setLoadMode = usePhysicsStore((s) => s.actions.setLoadMode);
  const [showMappingDebug, setShowMappingDebug] = useState(false);
  const [showDevToolsSection, setShowDevToolsSection] = useState(false);
  const [startupElapsedSeconds, setStartupElapsedSeconds] = useState(0);
  const [copyFailureReportState, setCopyFailureReportState] = useState("idle");
  const [failureReportText, setFailureReportText] = useState("");
  const [probeName, setProbeName] = useState("forearm_link");
  const errorDetailsRef = React.useRef(null);
  const wasmRuntimeInfo = React.useMemo(() => getMujocoWasmRuntimeInfo(), []);

  const actuatorMappingErrors = React.useMemo(() => {
    const failures = [];
    (diagnostics || []).forEach((entry) => {
      if (
        entry?.source !== "physics-init" ||
        entry?.stage !== "actuator-mapping" ||
        entry?.level !== "error"
      )
        return;
      const errors = Array.isArray(entry?.data?.errors) ? entry.data.errors : [entry?.message];
      errors.forEach((error) => {
        const message = String(error || "").trim();
        if (message && !failures.includes(message)) failures.push(message);
      });
    });
    return failures;
  }, [diagnostics]);
  const hasBodySyncFailure =
    sceneBodyCount > 0 && (isRunning || isPaused) && total > 0 && mapped === 0;
  const errorTitle = React.useMemo(() => physicsErrorTitle(lastError), [lastError]);
  const errorActionMessage = React.useMemo(() => physicsUserActionMessage(lastError), [lastError]);
  const isCapabilityUnavailable = React.useMemo(
    () => isPhysicsCapabilityUnavailableError(lastError),
    [lastError]
  );
  const copyPhysicsFailureReport = React.useCallback(async () => {
    try {
      if (!failureReportText) return;
      const copied = await copyTextToClipboard(failureReportText, "[PhysicsRuntimePanel]");
      if (!copied) {
        setCopyFailureReportState("manual");
        return;
      }
      setCopyFailureReportState("copied");
      window.setTimeout(() => setCopyFailureReportState("idle"), 1500);
    } catch (error) {
      logger.warn("[PhysicsRuntimePanel] Failed to copy physics failure report", error);
      setCopyFailureReportState("manual");
    }
  }, [failureReportText]);

  React.useEffect(() => {
    if (status !== "error" || !lastError) {
      setFailureReportText("");
      setCopyFailureReportState("idle");
      return undefined;
    }
    let cancelled = false;
    buildPhysicsFailureReportText()
      .then((reportText) => {
        if (!cancelled) setFailureReportText(reportText);
      })
      .catch((error) => {
        logger.warn("[PhysicsRuntimePanel] Failed to prepare physics failure report", error);
        if (!cancelled) setFailureReportText("");
      });
    return () => {
      cancelled = true;
    };
  }, [status, lastError]);

  React.useEffect(() => {
    if (status !== "starting" || !startupStartedAt) {
      setStartupElapsedSeconds(0);
      return undefined;
    }
    const updateElapsed = () => {
      setStartupElapsedSeconds(Math.max(0, Math.floor((Date.now() - startupStartedAt) / 1000)));
    };
    updateElapsed();
    const interval = window.setInterval(updateElapsed, 250);
    return () => window.clearInterval(interval);
  }, [status, startupStartedAt]);

  React.useEffect(() => {
    const focusErrorDetails = () => {
      window.setTimeout(() => {
        const el = errorDetailsRef.current;
        if (!el) return;
        el.scrollIntoView({ block: "center", behavior: "smooth" });
        el.focus({ preventScroll: true });
      }, 0);
    };
    window.addEventListener("physics-panel-focus-error", focusErrorDetails);
    return () => window.removeEventListener("physics-panel-focus-error", focusErrorDetails);
  }, []);

  return (
    <DraggablePanel
      open={open}
      onClose={onClose}
      title="Runtime Controls & Stats"
      titleIcon={titleIcon}
      initialSize={{ width: 360, height: 540 }}
      mobilePinPanelId={mobilePinPanelId}
    >
      <div className="p-3 space-y-2 overflow-y-auto flex-1 min-h-0">
        {status === "starting" && (
          <div className="mx-3 flex items-center gap-2 rounded border border-sky-700/70 bg-sky-950/35 px-3 py-2 text-sm text-sky-100">
            <span className="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-sky-300 border-t-transparent" />
            <span>Starting physics worker... {startupElapsedSeconds}s elapsed</span>
          </div>
        )}
        {status === "error" && (
          <div
            ref={errorDetailsRef}
            tabIndex={-1}
            className="mx-3 px-3 py-2 rounded text-sm"
            style={
              isCapabilityUnavailable
                ? {
                    background: "#451a03",
                    color: "#fde68a",
                    borderLeft: "3px solid #fbbf24",
                  }
                : {
                    background: "#450a0a",
                    color: "#fca5a5",
                    borderLeft: "3px solid #f87171",
                  }
            }
          >
            <div className="font-semibold">{errorTitle}</div>
            <div
              className={`mt-1 text-xs leading-5 ${
                isCapabilityUnavailable ? "text-amber-100" : "text-red-100"
              }`}
            >
              {errorActionMessage}
            </div>
            {lastError ? (
              <pre className="mt-2 max-h-28 overflow-auto whitespace-pre-wrap break-words rounded border border-red-900/60 bg-black/25 p-2 text-[11px] leading-4 text-red-50">
                {lastError}
              </pre>
            ) : null}
            <button
              type="button"
              className="mt-2 inline-flex items-center gap-1.5 rounded border border-red-700/70 px-2.5 py-1 text-xs text-red-50 hover:bg-red-950/50 disabled:cursor-not-allowed disabled:opacity-60"
              onClick={copyPhysicsFailureReport}
              disabled={!failureReportText}
            >
              <Clipboard className="h-3.5 w-3.5" />
              {!failureReportText
                ? "Preparing report"
                : copyFailureReportState === "copied"
                  ? "Copied report"
                  : copyFailureReportState === "manual"
                    ? "Select report below"
                    : "Copy physics failure report"}
            </button>
            {copyFailureReportState === "manual" && failureReportText ? (
              <textarea
                readOnly
                className="mt-2 h-32 w-full resize-y rounded border border-red-800/70 bg-black/30 p-2 font-mono text-[11px] leading-4 text-red-50"
                value={failureReportText}
                onFocus={(event) => event.currentTarget.select()}
                aria-label="Physics failure report"
              />
            ) : null}
          </div>
        )}
        {actuatorMappingErrors.length > 0 ? (
          <div className="mx-3 rounded border border-amber-700/70 bg-amber-950/30 px-3 py-2 text-xs text-amber-100">
            <div className="font-semibold">Some actuators failed to map.</div>
            <div className="mt-1 space-y-0.5">
              {actuatorMappingErrors.slice(0, 4).map((message) => (
                <div key={message}>{message}</div>
              ))}
            </div>
          </div>
        ) : null}
        {hasBodySyncFailure ? (
          <div className="mx-3 rounded border border-amber-700/70 bg-amber-950/30 px-3 py-2 text-xs text-amber-100">
            Simulation body names don't match scene - visuals may be out of sync.
          </div>
        ) : null}
        <PhysicsPanelPerformanceSection />
        <PhysicsRuntimeControlsSection
          motorGain={motorGain}
          realtimeScale={realtimeScale}
          setMotorGain={setMotorGain}
          setRealtimeScale={setRealtimeScale}
        />
        <PhysicsDevToolsSection
          engine={engine}
          exportWorkerXml={exportWorkerXml}
          isRunning={isRunning}
          loadMode={loadMode}
          mapped={mapped}
          poseNoGroupConversion={poseNoGroupConversion}
          probeName={probeName}
          setLoadMode={setLoadMode}
          setPoseNoGroupConversion={setPoseNoGroupConversion}
          setProbeName={setProbeName}
          setShowDevToolsSection={setShowDevToolsSection}
          setShowMappingDebug={setShowMappingDebug}
          showDevTools={showDevTools}
          showDevToolsSection={showDevToolsSection}
          showMappingDebug={showMappingDebug}
          total={total}
          wasmRuntimeInfo={wasmRuntimeInfo}
          worker={worker}
        />
      </div>
    </DraggablePanel>
  );
};

export default PhysicsRuntimePanel;
